import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, Form, Select } from 'antd';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type FC } from 'react';

import { useFormSchema } from '../../hooks/use-form-schema';
import {
  buildAssignablePermissionOptions,
  DEFAULT_TEAM_INVITE_PERMISSION,
} from '../../lib/permission-options';
import type { TeamPermissionFormValues } from '../../lib/types';
import styles from './create-form.module.scss';
import { CloseSmall } from '@shared/assets/icons/components';
import { Text } from '@shared/ui/antd';
import { buildAccessRights } from '@shared/constants';
import { useLazyCreateInviteLinkTeamCreateInviteLinkGetQuery } from '@shared/api';
import { copyToClipboard } from '@shared/utils/copy-to-clipboard';

interface CreateFormProps {
  organizationId: number;
  onClose?: () => void;
}

export const CreateForm: FC<CreateFormProps> = ({ organizationId, onClose }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'team' });
  const accessRights = buildAccessRights(t);
  const permissionOptions = buildAssignablePermissionOptions(t);
  const formSchema = useFormSchema();
  const [generateInviteLink, { isLoading }] = useLazyCreateInviteLinkTeamCreateInviteLinkGetQuery();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<TeamPermissionFormValues>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      permission: DEFAULT_TEAM_INVITE_PERMISSION,
    },
  });

  const selectedPermission =
    useWatch({
      control,
      name: 'permission',
    }) ?? DEFAULT_TEAM_INVITE_PERMISSION;

  const handleGenerateInviteLink = async (values: TeamPermissionFormValues) => {
    try {
      const response = await generateInviteLink({
        organizationId,
        role: values.permission,
      }).unwrap();

      const domain = window.location.origin;
      const inviteLink = `${domain}/invite/${response}`;

      await copyToClipboard(inviteLink);
    } catch {
      // Request errors are handled by the shared API layer. Avoid unhandled promise rejections here.
    }
  };

  return (
    <Flex vertical className={styles.root}>
      <Flex align="center" justify="space-between" className={styles.header}>
        <Text variant="2xl" fontWeight={600}>
          {t('modal.createTitle')}
        </Text>

        <CloseSmall onClick={onClose} className={styles.closeIcon} />
      </Flex>

      <div className={styles.divider} />

      <Form
        layout="vertical"
        onFinish={handleSubmit(handleGenerateInviteLink)}
        className={styles.form}
      >
        <Flex vertical gap="20px" className={styles.content}>
          <Text variant="sm" fontWeight={500} className={styles.subtitle}>
            {t('modal.createSubtitle')}
          </Text>

          <div className={styles.settingsCard}>
            <div className={styles.settingRow}>
              <Flex vertical gap="4px" className={styles.settingMeta}>
                <Text variant="sm" fontWeight={600}>
                  {t('modal.inviteTimeLabel')}
                </Text>
                <Text variant="xs" className={styles.settingHint}>
                  {t('modal.inviteTimeDescription')}
                </Text>
              </Flex>

              <Text variant="sm" fontWeight={600} className={styles.settingValueAccent}>
                {t('modal.inviteTimeTime')}
              </Text>
            </div>

            <div className={styles.settingRow}>
              <Flex vertical gap="4px" className={styles.settingMeta}>
                <Text variant="sm" fontWeight={600}>
                  {t('modal.permission')}
                </Text>

                <Text variant="xs" className={styles.settingHint}>
                  {accessRights[selectedPermission]?.description}
                </Text>
              </Flex>

              <Form.Item
                name="permission"
                help={errors.permission?.message}
                validateStatus={errors.permission ? 'error' : ''}
                className={styles.permissionField}
              >
                <Controller
                  name="permission"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      options={permissionOptions}
                      className={styles.permissionSelect}
                      // style={{ height: '48px' }}
                      size="middle"
                      popupClassName={styles.permissionDropdown}
                      optionRender={({ label, data }) => (
                        <Flex vertical gap="2px" className={styles.optionContent}>
                          <Text variant="sm" fontWeight={600}>
                            {label}
                          </Text>
                          <Text variant="xs" className={styles.optionDescription}>
                            {data.description}
                          </Text>
                        </Flex>
                      )}
                      placeholder={t('modal.selectPermissionPlaceholder')}
                      showSearch={false}
                    />
                  )}
                />
              </Form.Item>
            </div>

            <div className={styles.settingRow}>
              <Flex vertical gap="4px" className={styles.settingMeta}>
                <Text variant="sm" fontWeight={600}>
                  {t('modal.activateLabel')}
                </Text>
                <Text variant="xs" className={styles.settingHint}>
                  {t('modal.activateDescription')}
                </Text>
              </Flex>

              <Text variant="sm" fontWeight={600} className={styles.settingValue}>
                {t('modal.activateCount')}
              </Text>
            </div>
          </div>
        </Flex>

        <div className={styles.divider} />

        <Flex gap="16px" align="center" justify="center" className={styles.button_container}>
          <Button
            block
            className={styles.button}
            disabled={isLoading || isSubmitting || !organizationId}
            loading={isLoading || isSubmitting}
            type="primary"
            htmlType="submit"
          >
            {t('modal.createConfirm')}
          </Button>
        </Flex>
      </Form>
    </Flex>
  );
};
